import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Input, Spacer, VDBText } from "@components/atoms";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTranslation } from "react-i18next";
import InputDrawer from "@components/molecules/InputDrawer/InputDrawer";
import { HoldFormProps } from "../types";
import MobileHoursSlideSheet from "./HoursSlideSheet";
import { Keyboard, View } from "react-native";
import DropdownWithPortal from "./WebHoursSlideSheet";
import { useUserInfo } from "@redux/slices/userInfoSlice";

const HoldForm: React.FC<HoldFormProps> = ({
  holdForm,
  itemDetail,
  sendValues,
  selectedHoldHours,
  onApply,
  testIDPrefix = "bmh.holdForm",
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const [selectedHours, setSelectedHours] = useState("");
  const userInfo = useUserInfo();

  useEffect(() => {
    onApply(selectedHours);
  }, [selectedHours]);

  useEffect(() => {
    onHoldApply(selectedHoldHours);
  }, [selectedHoldHours]);

  const onHoldApply = (hours: string) => {
    holdForm.setValue("hold_hours", hours);
    setSelectedHours(hours);
    onApply(hours);
  };

  const hoursData = userInfo.user.feature_settings.max_hold_hours;

  const pickerItems = hoursData.map((h) => {
    const num = Number(h);
    return num > 72
      ? `${num / 24} ${t("bmh.hold.hour.type.days.text")}`
      : `${num} ${t("bmh.hold.hour.type.hour.text")}`;
  });

  const handleSelect = (label: string) => {
    setSelectedHours(label);
  };

  return (
    <>
      <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
        {t("bmh.hold.screenText.text")}
      </VDBText>
      <Spacer size={SpaceType.Double} />

      {!theme.isLargeScreen ? (
        <View
          style={{ width: "100%" }}
          testID={`${testIDPrefix}.hoursPicker.mobile`}
        >
          <InputDrawer
            children={
              <MobileHoursSlideSheet
                itemDetail={itemDetail}
                onApply={onHoldApply}
                closeModal={() => {}}
                selectedHours={selectedHours}
              />
            }
            itemText={selectedHoldHours}
            headerTitle={t("bmh.hold.hour.title.text")}
          />
        </View>
      ) : (
        <View
          style={{ zIndex: 10000 }}
          testID={`${testIDPrefix}.hoursPicker.web`}
        >
          <DropdownWithPortal
            items={pickerItems}
            selected={selectedHours}
            onSelect={handleSelect}
            placeholder="Select hold time"
            screenText={""}
          />
        </View>
      )}

      <Spacer size={SpaceType.Big} />
      <Controller
        control={holdForm.control}
        render={({
          field: { onChange, onBlur, value },
          formState: { errors },
        }) => (
          <Input
            testID={`${testIDPrefix}.input.holdComments`}
            inputLabel={t("bmh.input.comments.placeHolder.text")}
            placeholder={t("bmh.input.comments.placeHolder.text")}
            leftIcon={undefined}
            hideIconContainer={true}
            onBlur={onBlur}
            onChangeText={(val) => {
              onChange(val);
              sendValues();
            }}
            value={value}
            error={(errors?.holdComments?.message ?? "") as string}
            multiline={true}
            numberOfLines={4}
            {...(!theme.isLargeScreen && {
              blurOnSubmit: true,
              onSubmitEditing: () => {
                Keyboard.dismiss();
              },
            })}
            keyboardType="default"
            returnKeyType="done"
          />
        )}
        name="holdComments"
      />
    </>
  );
};

export default HoldForm;
